import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { AssetManager, SpineExtended, container, events, isInternationalBuild } from '@game-studio/arcade-base';
import { gameSettings } from '../../../models/game-settings';
import { GameState, gameModel } from '../../../models/game-model';
import { CollectorState, collectorService } from '../../../services/collector-service';
import gsap from 'gsap';
import { ISkeletonData } from 'pixi-spine';
import { ICollectorPanel } from './collector-view';
import { optimizationService } from '@/src/core/services/optimization-service';
import { gameEventEmitter } from '@/src/events';
import { IDONDStrings, ISkinConfigEx } from '@/src/types';

export class PanelPortraitView extends Container {
    private _prizePanelConfig: ICollectorPanel;

    private _cellView: SpineExtended;
    private _boundBox: Graphics;

    private _valueText: Text;
    private _toGoText: Text;

    private _cellStates: number[];
    private _oneToGo: boolean;
    private _win: boolean;

    private _valueFontSize: number = 38;

    constructor(prizePanelConfig: ICollectorPanel) {
        super();
        this._prizePanelConfig = prizePanelConfig;
        this.buildCollector(prizePanelConfig);
        this.buildValueLabel();
        this.buildToGoLabel();
        this.addListeners();
    }

    // Hide elements before drawing.
    public preDraw(): void {
        this._cellView.visible = false;
        this._valueText.visible = false;
        this._toGoText.visible = false;
    }

    // Show elements after drawing.
    public postDraw(): void {
        this._cellView.visible = true;
        this._valueText.visible = true;
        this._toGoText.visible = true;
    }

    private buildCollector(prizePanelConfig: ICollectorPanel): void {
        // create spine asset
        const spineData: ISkeletonData = AssetManager.getSpineResourceData('collectors');
        const cell = new SpineExtended(spineData);
        this.addChild(cell);

        // set up spine asset
        cell.skeleton.setSkinByName(prizePanelConfig.key + 'Portrait');
        cell.state.setAnimation(0, 'sizer', true);

        // store in array
        this._cellView = cell;

        this.positionCellsAndBounds();
        this._cellStates = new Array(prizePanelConfig.cells).fill(CollectorState.Available);
    }

    private positionCellsAndBounds(): void {
        // Create bounding sizer container.
        this.initSizer();

        // Render sizer from cell.
        const cell = this._cellView;
        this.buildSizer(cell);

        // Set cell spine to idle state.
        cell.state.setAnimation(0, 'idle', true);

        // End building the sizer.
        this._boundBox.endFill();

        // Add Optimization.
        optimizationService.optimizeOn(cell);
    }

    private initSizer(): void {
        this._boundBox = new Graphics();
        this.addChild(this._boundBox);
    }

    private buildSizer(referenceSpine: SpineExtended): void {
        this._boundBox.beginFill(0xffffff, 0);
        this._boundBox.drawRect(referenceSpine.x - referenceSpine.width / 2, referenceSpine.y - referenceSpine.height / 2, referenceSpine.width, referenceSpine.height);
    }

    private updateToGoValue(): void {
        const collectorPanelState = gameModel.collectorStates[this._prizePanelConfig.id];
        const toGoValue: number = collectorService.toGoCount(collectorPanelState);
        const gamestrings = (container.strings as IDONDStrings).gameStrings;
        const toGoText: string = toGoValue === 0 ? gamestrings.winText : gamestrings.toGo.replace('{value}', `${toGoValue}`);
        this._toGoText.text = toGoText;
        this.animateToGoTextScaling();
    }

    private animateToGoTextScaling(): void {
        if (gameModel.gameState !== GameState.IN_PLAY) {
            return;
        }
        // Apply a scaling animation.
        const animationInTime: number = 0.2;
        const animationOutTime: number = 0.3;
        const animationScale: number = 1.2;
        gsap.to(this._toGoText.scale, {
            duration: animationInTime,
            x: animationScale,
            y: animationScale,
            onComplete: () => {
                gsap.to(this._toGoText.scale, { duration: animationOutTime, x: 1, y: 1 });
            }
        });
    }

    private updateCells(collectorPanelState: CollectorState[]): void {
        for (let $panelStateIndex = 0; $panelStateIndex < collectorPanelState.length; $panelStateIndex++) {
            const modelState = collectorPanelState[$panelStateIndex];
            const cellState = this._cellStates[$panelStateIndex];

            if (modelState === CollectorState.Allocated && cellState === CollectorState.Available) {
                optimizationService.optimizeOff(this._cellView);
                this._cellStates[$panelStateIndex] = CollectorState.Allocated;
                this._cellView.state.setAnimation(0, 'collected', false);
                this._cellView.setAnimationPromise('collected', { trackIndex: 1 }).then(() => {
                    optimizationService.optimizeOn(this._cellView);
                });
                gsap.delayedCall(0.7, () => this.updateToGoValue());
            }
        }
    }

    private handleOneToGo(collectorPanelState: CollectorState[]): void {
        if (this._oneToGo || !collectorService.isOneToGo(collectorPanelState)) {
            return;
        }
        this._oneToGo = true;

        // Play one to go animation sequence.
        optimizationService.optimizeOff(this._cellView);
        this._cellView.setAnimationPromise('oneToGo', { trackIndex: 1 }).then(() => {
            this._cellView.state.setAnimation(1, 'oneToGoLoop', true);
        });
    }

    private handleWin(collectorPanelState: CollectorState[]): void {
        if (this._win || !collectorService.isWin(collectorPanelState)) {
            return;
        }
        this._win = true;

        // Determine the type of win.
        const winType = gameModel.goldPrize === this._prizePanelConfig.id ? 'goldenWin' : 'win';

        // Play win animation sequence.
        optimizationService.optimizeOff(this._cellView);
        this._cellView.setAnimationPromise(winType, { trackIndex: 1 }).then(() => {
            this._cellView.state.setAnimation(1, `${winType}Loop`, true);
            this.updateToGoValue();
        });
    }

    private buildValueLabel(): void {
        const style = new TextStyle({
            dropShadow: true,
            dropShadowBlur: 2,
            dropShadowDistance: 1,
            fontWeight: 'bolder',
            fontFamily: 'Wallop-Reg',
            fill: '#ffffff',
            fontSize: this._valueFontSize,
            lineJoin: 'round',
            miterLimit: 29,
            strokeThickness: 2,
            stroke: '#000000',
            whiteSpace: 'normal',
            wordWrap: true,
            wordWrapWidth: 200
        });

        this._valueText = new Text('£0000', style);
        this.addChild(this._valueText);
        this.updatePrizeValue$();
    }

    private buildToGoLabel(): void {
        const style = new TextStyle({
            dropShadow: true,
            dropShadowAlpha: 0.3,
            dropShadowAngle: 0.6,
            dropShadowBlur: 4,
            dropShadowDistance: 1,
            fill: '#ffffff',
            fontSize: 34,
            lineJoin: 'round',
            fontWeight: 'bold',
            fontFamily: 'Wallop-Reg',
            miterLimit: 29,
            stroke: '#f5f5f5',
            whiteSpace: 'normal',
            wordWrap: true,
            wordWrapWidth: 200
        });

        this._toGoText = new Text('6tg', style);
        this.addChild(this._toGoText);
        this._toGoText.y = this._toGoText.height * (container.skin.config as ISkinConfigEx).toGoTextHeightMultiplier;
        this._toGoText.anchor.set(0.5);
        this.updateToGoValue();
    }

    private updatePrizeValue$(): void {
        const prizeValue: number = container.stake.currentStake * gameSettings.winMultipliers[this._prizePanelConfig.id + 1];
        const prizeText: string = container.currencyFormatter.precise(prizeValue);
        this._valueText.text = prizeText;


        if (isInternationalBuild()) { // Taken from legacy-international-branch
            const textscale = Math.min(1, 1 - ((prizeText.length - 4) * 0.1));
            this._valueText.anchor.set(0.5);
            this._valueText.style.fontSize = this._valueFontSize * textscale;
            this._valueText.y = -this._boundBox.height * 0.75;
        } else {
            this._valueText.x = -this._valueText.width * 0.5;
            this._valueText.y = -this._valueText.height * 1.1;
        }
    }

    private onCollectorUpdated$(): void {
        const collectorPanelState = gameModel.collectorStates[this._prizePanelConfig.id];
        this.updateCells(collectorPanelState);
        this.handleOneToGo(collectorPanelState);
        this.handleWin(collectorPanelState);
    }

    private onNewGame$(): void {
        // Clear existing win animations loops.
        if (this._win) {
            this._cellView.setAnimationPromise('winStop', { trackIndex: 1 }).then(() => {
                optimizationService.optimizeOn(this._cellView);
            });
        } else {
            this._cellView.state.setAnimation(1, 'idle', false);
            optimizationService.optimizeOn(this._cellView);
        }

        // Reset local states.
        this._oneToGo = false;
        this._win = false;
        this._cellStates = new Array(this._prizePanelConfig.cells).fill(CollectorState.Available);

        // Call updatables.
        this.onCollectorUpdated$();
        this.updateToGoValue();
    }

    private onEndGame$(): void {
        // Turn off one to go loops.
        if (this._oneToGo && !this._win) {
            this._cellView.state.setAnimation(1, 'oneToGoStop', false);
        }
    }

    private addListeners(): void {
        gameEventEmitter.on('NEW_GAME', this.onNewGame$.bind(this));
        events.GAME.COMPLETE.addListener(this.onEndGame$.bind(this));
        events.CABINET.STAKE_CHANGE.addListener(() => {
            this.updatePrizeValue$();
            this.onNewGame$();
        });
        gameModel.bindToValue(
            this,
            () => gameModel.collectorStates,
            () => this.onCollectorUpdated$
        );
    }
}
